import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, ArrowLeft } from "lucide-react";
import { useUser } from "@clerk/clerk-react";
import { Link } from "wouter";

export default function Exosomes() {
  const { isLoaded, user } = useUser();
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      title: "Exosomes in Longevity, Functional Medicine, and Health Optimization: A Clinician's Introduction",
      subtitle: "Nature's Nanomessengers for Health, Regeneration, and Healthy Aging",
      content: (
        <div className="text-center space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Exosomes in Longevity, Functional Medicine, and Health Optimization
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              A Clinician's Introduction
            </p>
            <p className="text-lg text-blue-600 font-medium">
              Nature's Nanomessengers for Health, Regeneration, and Healthy Aging
            </p>
          </div>
          
          <div className="bg-gradient-to-r from-green-50 to-blue-50 p-8 rounded-lg">
            <div className="flex justify-center">
              <svg width="400" height="200" viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .title-text { font-family: Arial, sans-serif; font-size: 16px; font-weight: bold; text-anchor: middle; }
                  .subtitle-text { font-family: Arial, sans-serif; font-size: 12px; text-anchor: middle; }
                  .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 2; }
                  .cell { fill: #87CEEB; stroke: #4682B4; stroke-width: 2; }
                `}</style>
                
                <text x="200" y="25" className="title-text">Exosome-Mediated Intercellular Communication</text>
                
                <circle cx="80" cy="100" r="40" className="cell"/>
                <text x="80" y="105" className="subtitle-text" fill="white" fontWeight="bold">Source Cell</text>
                
                <circle cx="150" cy="80" r="8" className="exosome"/>
                <circle cx="170" cy="90" r="8" className="exosome"/>
                <circle cx="160" cy="110" r="8" className="exosome"/>
                <text x="160" y="140" className="subtitle-text">Exosomes</text>
                
                <circle cx="320" cy="100" r="40" className="cell"/>
                <text x="320" y="105" className="subtitle-text" fill="white" fontWeight="bold">Target Cell</text>
                
                <path d="M120 100 Q200 70 280 100" stroke="#4CAF50" strokeWidth="3" fill="none" markerEnd="url(#arrowhead)"/>
                
                <text x="200" y="185" className="subtitle-text" fontWeight="bold" fill="#2E7D32">
                  Therapeutic Intercellular Communication
                </text>
                
                <defs>
                  <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                    <polygon points="0 0, 10 3.5, 0 7" fill="#4CAF50"/>
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Introduction: Exosomes in Longevity Medicine - A New Frontier",
      subtitle: "The Emerging Role of Exosomes",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <p className="text-sm text-blue-800 mb-4">
              Welcome! This session explores exosomes: once considered cellular debris, now recognized as key intercellular messengers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Longevity Medicine Focus</h3>
                <p className="text-sm text-green-800">
                  Understanding how exosomes influence aging hallmarks (oxidative stress, inflammation, senescence) and offer therapeutic potential.
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Functional Medicine Angle</h3>
                <p className="text-sm text-purple-800">
                  Exosomes as mediators and biomarkers in complex biological systems.
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Health Optimization Goal</h3>
                <p className="text-sm text-orange-800">
                  Leveraging exosomes for regeneration, improved physiological function, and extended healthspan.
                </p>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Key Shift</h3>
                <p className="text-sm text-yellow-800">
                  Moving from "cell-based" to "cell-free" therapies, mitigating risks of live cell administration (immune rejection, tumorigenicity).
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Defining Exosomes: Biogenesis, Composition, Characteristics",
      subtitle: "What Are Exosomes? Nature's Nanomessengers",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Definition & Biogenesis</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Definition:</strong> Nano-sized (40-160 nm) extracellular vesicles (EVs) of endosomal origin.</p>
                  <p><strong>Biogenesis:</strong> Formed within multivesicular bodies (MVBs) via inward budding of the endosomal membrane; released upon MVB fusion with the plasma membrane.</p>
                  <p className="text-xs italic">Distinguishes them from microvesicles (plasma membrane budding) & apoptotic bodies.</p>
                  <p><strong>Ubiquitous Source:</strong> Secreted by nearly all cell types.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Composition (Key to Function)</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Lipid Bilayer:</strong> Protects cargo, facilitates uptake.</p>
                  <p><strong>Proteins:</strong> Enzymes, growth factors, signaling molecules, tetraspanins (CD9, CD63, CD81), Alix, TSG101.</p>
                  <p><strong>Nucleic Acids:</strong> mRNA, microRNAs (miRNAs), lncRNAs, circRNAs, DNA fragments. Modulate recipient cell gene expression.</p>
                  <p><strong>Metabolites:</strong> Contribute to signaling.</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Exosome Biogenesis</h4>
                <div className="flex justify-center">
                  <svg width="400" height="250" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                      .arrow { fill: #555; }
                      .cell-membrane { fill: #87CEEB; stroke: #4682B4; stroke-width: 1.5; }
                      .endosome-membrane { fill: #FFD700; stroke: #DAA520; stroke-width: 1; }
                      .mvb-membrane { fill: #90EE90; stroke: #3CB371; stroke-width: 1.5; }
                      .ilv { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 0.5; }
                      .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 1; }
                      .cargo-protein { fill: #ADD8E6; }
                      .cargo-rna { fill: #FFB6C1; }
                    `}</style>
                    
                    <rect x="10" y="10" width="380" height="230" className="cell-membrane" rx="10" ry="10"/>
                    <text x="15" y="25" className="label">Parent Cell</text>
                    
                    <ellipse cx="70" cy="70" rx="30" ry="20" className="endosome-membrane"/>
                    <text x="50" y="100" className="label">Endosome</text>
                    <path d="M100 70 L130 70" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                    
                    <ellipse cx="180" cy="70" rx="40" ry="30" className="mvb-membrane"/>
                    <circle cx="170" cy="65" r="5" className="ilv"/>
                    <circle cx="190" cy="60" r="4" className="ilv"/>
                    <circle cx="180" cy="80" r="6" className="ilv"/>
                    <text x="150" y="110" className="label">Multivesicular Body (MVB)</text>
                    <text x="165" y="50" className="label">ILVs</text>
                    <path d="M220 70 L250 70" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                    
                    <path d="M260,70 C260,40 320,40 320,70" fill="none" className="mvb-membrane" strokeDasharray="3,3"/>
                    <text x="270" y="30" className="label">Fusion with Plasma Membrane</text>
                    <circle cx="300" cy="90" r="10" className="exosome"/>
                    <circle cx="303" cy="88" r="1.5" className="cargo-protein"/>
                    <path d="M298 92 Q 300 90 302 92" stroke="#FFB6C1" fill="none" strokeWidth="1"/>
                    <text x="320" y="95" className="label">Released Exosome</text>
                    
                    <g transform="translate(150, 150)">
                      <circle cx="0" cy="0" r="30" className="exosome"/>
                      <path d="M0,-30 A30,30 0 0,0 0,30" fill="#FFDAB9" stroke="#CD5C5C" strokeWidth="1"/>
                      <circle cx="-10" cy="-5" r="3" className="cargo-protein"/>
                      <text x="-12" y="3" className="label" fontSize="8px">Protein</text>
                      <path d="M5 0 Q 8 -5 11 0" stroke="#FFB6C1" fill="none" strokeWidth="1.5"/>
                      <text x="5" y="12" className="label" fontSize="8px">miRNA</text>
                      <ellipse cx="0" cy="15" rx="8" ry="3" fill="#F0E68C"/>
                      <text x="-5" y="25" className="label" fontSize="8px">Lipid</text>
                      <text x="-25" y="-35" className="label">Exosome Cargo</text>
                    </g>
                    
                    <defs>
                      <marker id="arrowhead" markerWidth="5" markerHeight="3.5" refX="0" refY="1.75" orient="auto">
                        <polygon points="0 0, 5 1.75, 0 3.5" className="arrow" />
                      </marker>
                    </defs>
                  </svg>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">Source Matters:</h4>
                <p className="text-sm text-yellow-800">
                  Cargo reflects parent cell type and state. "Young" or "healthy" cell-derived exosomes (e.g., MSCs) are preferred for regenerative/longevity purposes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Exosomes in Intercellular Communication & Longevity Relevance",
      subtitle: "Critical Mediators of Cell-to-Cell Talk",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Mechanism</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>Transfer bioactive cargo to recipient cells via:</p>
                  <ol className="ml-4 space-y-1">
                    <li>1. Surface receptor binding</li>
                    <li>2. Direct membrane fusion</li>
                    <li>3. Endocytosis</li>
                  </ol>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Outcome</h3>
                <p className="text-sm text-green-800">
                  Modulate recipient cell's gene expression, protein activity, proliferation, differentiation, survival.
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Scope</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Paracrine:</strong> Adjacent cells.</p>
                  <p><strong>Endocrine-like:</strong> Distant cells via circulation (blood, saliva, urine, CSF).</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Relevance to Longevity & Health Optimization</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <ul className="space-y-1">
                    <li>• Systemic reach allows for organism-wide influence on aging processes</li>
                    <li>• Potential for broad anti-aging effects, not just localized repair</li>
                    <li>• Exosomal cargo in biofluids: Promising source of systemic health biomarkers</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Exosome Uptake Mechanisms</h4>
                <div className="flex justify-center">
                  <svg width="350" height="200" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      .cell { fill: #ADD8E6; stroke: #87CEEB; stroke-width: 1.5; }
                      .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 1; }
                      .receptor { fill: #32CD32; stroke: #228B22; stroke-width: 1; }
                      .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                    `}</style>
                    
                    <rect x="50" y="50" width="250" height="120" className="cell" rx="10"/>
                    <text x="60" y="70" className="label">Recipient Cell</text>
                    
                    <circle cx="100" cy="30" r="8" className="exosome"/>
                    <rect x="95" y="45" width="10" height="5" className="receptor"/>
                    <text x="80" y="15" className="label">Receptor Binding</text>
                    
                    <circle cx="175" cy="25" r="8" className="exosome"/>
                    <path d="M175,33 Q175,40 180,45" stroke="#CD5C5C" strokeWidth="2" fill="none"/>
                    <text x="160" y="10" className="label">Membrane Fusion</text>
                    
                    <circle cx="250" cy="30" r="8" className="exosome"/>
                    <circle cx="250" cy="60" r="12" fill="none" stroke="#87CEEB" strokeWidth="2" strokeDasharray="2,2"/>
                    <circle cx="250" cy="60" r="6" className="exosome"/>
                    <text x="230" y="15" className="label">Endocytosis</text>
                    
                    <text x="125" y="190" className="label" fontWeight="bold">Multiple Uptake Pathways</text>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Mechanism 1: Immune Modulation (e.g., Macrophage Polarization)",
      subtitle: "Orchestrating Immune Responses",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">MSC-Derived Exosomes</h3>
                <p className="text-sm text-blue-800 mb-3">
                  MSC-derived exosomes are potent immunomodulators.
                </p>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Key Action: Macrophage Polarization:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Exosomal chemokines (e.g., CXCL12, CCL2) interact with macrophage receptors (e.g., CCR2)</li>
                    <li>• Promote shift from M1 (pro-inflammatory) to M2 (anti-inflammatory, pro-resolving, regenerative) phenotype</li>
                    <li>• Often mediated by upregulating IL-10 in macrophages</li>
                  </ul>
                  <p className="italic">"Re-educates" immune cells to foster a regenerative environment.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Relevance to "Inflammaging"</h3>
                <p className="text-sm text-green-800">
                  Chronic low-grade inflammation is a hallmark of aging. Exosomes can counteract this by promoting M2 macrophages.
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Clinical Insight</h3>
                <p className="text-sm text-purple-800 mb-2">
                  More sophisticated than single anti-inflammatory drugs; changes functional state of key immune cells.
                </p>
                <p className="text-sm text-purple-800">
                  <strong>Monitoring:</strong> IL-10 levels, macrophage phenotype markers (e.g., CD163 for M2, CD86 for M1) can assess therapeutic response.
                </p>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">M1/M2 Macrophage Polarization</h4>
              <div className="flex justify-center">
                <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                    .arrow { stroke: #555; stroke-width: 2; marker-end: url(#arrowhead); }
                    .macrophage { fill: #D3D3D3; stroke: #A9A9A9; stroke-width: 1.5; }
                    .m1 { fill: #FF6347; stroke: #DC143C; }
                    .m2 { fill: #32CD32; stroke: #228B22; }
                    .exosome { fill: #FFA07A; stroke: #CD5C5C; }
                  `}</style>
                  
                  <circle cx="70" cy="100" r="20" className="exosome"/>
                  <text x="40" y="135" className="label">MSC-Exosome</text>
                  <text x="30" y="150" className="label">(CXCL12, CCL2)</text>
                  
                  <ellipse cx="200" cy="100" rx="40" ry="30" className="macrophage"/>
                  <text x="170" y="105" className="label">Macrophage</text>
                  
                  <line x1="90" y1="100" x2="160" y2="100" className="arrow"/>
                  
                  <ellipse cx="330" cy="50" rx="35" ry="25" className="m1"/>
                  <text x="300" y="45" className="label">M1 Phenotype</text>
                  <text x="290" y="65" className="label">(Pro-inflammatory)</text>
                  <text x="295" y="80" className="label" fontSize="10px">TNF-α, IL-6 ↑</text>
                  <path d="M230,85 Q260,60 300,50" stroke="#555" strokeWidth="1.5" fill="none" strokeDasharray="4,2"/>
                  
                  <ellipse cx="330" cy="150" rx="35" ry="25" className="m2"/>
                  <text x="300" y="145" className="label">M2 Phenotype</text>
                  <text x="280" y="165" className="label">(Anti-inflammatory)</text>
                  <text x="295" y="180" className="label" fontSize="10px">IL-10, Arg1 ↑</text>
                  <path d="M230,115 Q270,140 300,150" stroke="#555" strokeWidth="2.5" fill="none" className="arrow"/>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="0" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#555"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Mechanism 2: Gene Regulation via Non-Coding RNAs",
      subtitle: "The Power of Exosomal Cargo: ncRNAs",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Non-Coding RNAs in Exosomes</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>Exosomes are rich in non-coding RNAs (ncRNAs): microRNAs (miRNAs), lncRNAs, circRNAs.</p>
                  <p><strong>miRNAs:</strong> ~22 nucleotide RNAs, bind to target mRNAs → degradation or translational repression → fine-tune gene expression.</p>
                  <p><strong>lncRNAs/circRNAs:</strong> Can act as "sponges" for miRNAs, adding regulatory complexity.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Selective Sorting</h3>
                <p className="text-sm text-green-800">
                  Specific ncRNAs are actively packaged into exosomes, implying directed biological purpose.
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Impact</h3>
                <p className="text-sm text-purple-800">
                  Exosomal ncRNAs alter recipient cell migration, apoptosis, proliferation, autophagy – fundamental to tissue repair, immunity, aging.
                </p>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Clinical Relevance</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p><strong>Biomarkers:</strong> ncRNA profiles vary with cell type/condition. Distinct exosomal miRNA signatures linked to diseases (cancer, CVD) and treatment response.</p>
                  <p><strong>Precision Medicine:</strong> Potential to engineer exosomes to deliver therapeutic ncRNAs or target pathogenic ones.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">miRNA Action Mechanism</h4>
              <div className="flex justify-center">
                <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 12px; fill: #333; }
                    .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 1.5; }
                    .mirna { fill: #FFB6C1; stroke: #FF69B4; stroke-width: 1; }
                    .cell { fill: #ADD8E6; stroke: #87CEEB; stroke-width: 1.5; }
                    .mrna { stroke: #4CAF50; stroke-width: 2; }
                    .ribosome { fill: #DDA0DD; }
                  `}</style>
                  
                  <circle cx="50" cy="100" r="25" className="exosome"/>
                  <text x="30" y="135" className="label">Exosome</text>
                  <ellipse cx="50" cy="100" rx="8" ry="4" className="mirna"/>
                  <text x="40" y="95" className="label" fontSize="10px">miRNA</text>
                  <line x1="75" y1="100" x2="120" y2="100" stroke="#555" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                  
                  <rect x="120" y="30" width="270" height="140" className="cell" rx="10"/>
                  <text x="130" y="50" className="label">Recipient Cell</text>
                  
                  <path d="M150,80 C180,70 210,90 240,80 S300,70 330,80" className="mrna" fill="none"/>
                  <text x="230" y="70" className="label">mRNA</text>
                  
                  <ellipse cx="200" cy="83" rx="8" ry="4" className="mirna"/>
                  <text x="160" y="110" className="label">miRNA binds mRNA</text>
                  
                  <path d="M200,90 L200,120 L160,140" stroke="#555" strokeWidth="1.5" fill="none" markerEnd="url(#arrowhead)"/>
                  <text x="70" y="150" className="label">mRNA Degradation</text>
                  <path d="M240,80 C240,100 280,120 300,140" stroke="#555" strokeWidth="1.5" fill="none" markerEnd="url(#arrowhead)"/>
                  <text x="280" y="150" className="label">Translational Repression</text>
                  <circle cx="330" cy="110" r="10" className="ribosome"/>
                  <text x="315" y="130" className="label" fontSize="10px">(No Protein)</text>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Core Mechanism 3: Stimulating Tissue Regeneration & Growth",
      subtitle: "Driving Tissue Repair and Rejuvenation",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Regenerative Capacity</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Exosomes, especially from stem cells (e.g., MSCs), are direct instigators of tissue regeneration.
                </p>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Cargo:</strong> Rich in growth factors (VEGF, FGF), signaling proteins, regulatory RNAs.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Actions</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <ul className="space-y-1">
                    <li>• Stimulate cell proliferation, differentiation, and migration</li>
                    <li>• Promote angiogenesis (new blood vessel formation)</li>
                    <li>• Modulate extracellular matrix (ECM) production (e.g., increase collagen/elastin, decrease MMPs in skin)</li>
                    <li>• Support cartilage and bone regeneration</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Clinical Insight</h3>
                <p className="text-sm text-purple-800 mb-2 italic">
                  "The benefit of the exosome should be instantiating growth, not stopping inflammation. They do certainly do both."
                </p>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>• Dual capability: Reduce inflammatory barriers AND provide signals/building blocks for new tissue</p>
                  <p>• More holistic than therapies targeting only inflammation or only growth factors</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Examples</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <ul className="space-y-1">
                    <li>• Skin rejuvenation</li>
                    <li>• Orthopedic applications</li>
                    <li>• Wound healing</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Tissue Regeneration Process</h4>
                <div className="flex justify-center">
                  <svg width="350" height="180" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                      .tissue { fill: #FFB6C1; stroke: #FF69B4; stroke-width: 1; }
                      .vessel { fill: #FF4500; stroke: #DC143C; stroke-width: 1; }
                      .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 1; }
                      .growth-factor { fill: #32CD32; }
                    `}</style>
                    
                    <text x="175" y="20" className="label" fontSize="14px" fontWeight="bold">Before</text>
                    <rect x="50" y="30" width="100" height="60" className="tissue" opacity="0.6"/>
                    <text x="70" y="65" className="label">Damaged Tissue</text>
                    
                    <circle cx="200" cy="60" r="8" className="exosome"/>
                    <circle cx="210" cy="50" r="3" className="growth-factor"/>
                    <text x="180" y="80" className="label">Exosome + Growth Factors</text>
                    
                    <text x="175" y="110" className="label" fontSize="14px" fontWeight="bold">After</text>
                    <rect x="50" y="120" width="120" height="50" className="tissue"/>
                    <circle cx="80" cy="130" r="3" className="vessel"/>
                    <circle cx="100" cy="140" r="3" className="vessel"/>
                    <circle cx="120" cy="135" r="3" className="vessel"/>
                    <text x="60" y="155" className="label">Regenerated Tissue</text>
                    <text x="70" y="165" className="label">+ New Vasculature</text>
                    
                    <path d="M150 60 Q175 85 150 120" stroke="#4CAF50" strokeWidth="2" markerEnd="url(#arrowhead)" fill="none"/>
                    
                    <defs>
                      <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                        <polygon points="0 0, 8 3, 0 6" fill="#4CAF50"/>
                      </marker>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Exosomes & Key Aging Pathways: Modulating the Hallmarks",
      subtitle: "Influencing Major Signaling Pathways in Aging",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <p className="text-sm text-blue-800 mb-4">
              Aging is driven by dysregulation of key cellular signaling pathways. 
              Exosomes interact with these pathways by delivering specific proteins, lipids, or nucleic acids (especially miRNAs, lncRNAs).
            </p>
            <p className="text-sm text-blue-800 font-medium">
              This positions exosomes as:
            </p>
            <ul className="ml-4 space-y-1 text-sm text-blue-800">
              <li>• <strong>Diagnostic Indicators:</strong> Exosomal cargo can reflect pathway activity</li>
              <li>• <strong>Therapeutic Levers:</strong> Exosomes can be used to modulate these pathways</li>
            </ul>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-green-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-green-900 mb-2">Insulin/IGF-1 (IIS) & mTOR</h4>
              <p className="text-xs text-green-800">Metabolism & Growth</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-purple-900 mb-2">AMPK & Sirtuins/NAD+</h4>
              <p className="text-xs text-purple-800">Energy Sensing</p>
            </div>
            <div className="bg-orange-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-orange-900 mb-2">NF-κB & Nrf2</h4>
              <p className="text-xs text-orange-800">Inflammation & Redox</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-red-900 mb-2">p53/p16INK4a</h4>
              <p className="text-xs text-red-800">Cellular Senescence</p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-yellow-900 mb-2">JAK/STAT & TGF-β</h4>
              <p className="text-xs text-yellow-800">Immunity & Fibrosis</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-lg text-center">
              <h4 className="font-semibold text-gray-900 mb-2">RAAS</h4>
              <p className="text-xs text-gray-800">Biomarker Source</p>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Exosome-Pathway Interactions</h4>
            <div className="flex justify-center">
              <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                  .exosome { fill: #FFA07A; stroke: #CD5C5C; stroke-width: 2; }
                  .pathway { fill: #87CEEB; stroke: #4682B4; stroke-width: 1; }
                `}</style>
                
                <circle cx="200" cy="100" r="30" className="exosome"/>
                <text x="200" y="105" className="label" textAnchor="middle" fontWeight="bold">Exosome</text>
                
                <circle cx="100" cy="60" r="20" className="pathway"/>
                <text x="100" y="65" className="label" textAnchor="middle" fontSize="8px">mTOR</text>
                <path d="M170,85 L120,70" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                
                <circle cx="300" cy="60" r="20" className="pathway"/>
                <text x="300" y="65" className="label" textAnchor="middle" fontSize="8px">AMPK</text>
                <path d="M230,85 L280,70" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                
                <circle cx="100" cy="140" r="20" className="pathway"/>
                <text x="100" y="145" className="label" textAnchor="middle" fontSize="8px">NF-κB</text>
                <path d="M170,115 L120,130" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                
                <circle cx="300" cy="140" r="20" className="pathway"/>
                <text x="300" y="145" className="label" textAnchor="middle" fontSize="8px">Nrf2</text>
                <path d="M230,115 L280,130" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                
                <defs>
                  <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pathways (Part 1): IIS, mTOR, AMPK – Metabolism & Growth",
      subtitle: "Modulating Metabolism, Growth & Energy Sensing",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">Insulin/IGF-1 Signaling (IIS)</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p>Regulates growth, metabolism, lifespan. Dysregulation (e.g., insulin resistance) linked to age-related diseases.</p>
                <p><strong>Exosome Interaction:</strong> Cord blood exosome proteome predicts later insulin resistance. Exosomes carry IIS-modulating miRNAs/proteins.</p>
                <p><strong>Clinical:</strong> Exosomal biomarkers for insulin resistance risk. Engineered exosomes to enhance insulin sensitivity.</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">mTOR Pathway</h3>
              <div className="space-y-2 text-sm text-green-800">
                <p>Central regulator of cell growth, proliferation, autophagy. Overactivation linked to aging.</p>
                <p><strong>Exosome Interaction:</strong> mTORC1 regulates exosome biogenesis/secretion. Exosomal miRNAs (e.g., miR-126) can modulate mTOR in recipient cells.</p>
                <p><strong>Clinical:</strong> Exosome profiles as markers in mTOR-related diseases. Modulating exosome cargo via mTOR (e.g., rapamycin).</p>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">AMPK Pathway</h3>
              <div className="space-y-2 text-sm text-purple-800">
                <p>Cellular energy sensor; activation generally promotes health/longevity.</p>
                <p><strong>Exosome Interaction:</strong> Exosomal miRNAs (e.g., miR-126) can activate AMPK. Involved in exosome-mediated autophagy regulation.</p>
                <p><strong>Clinical:</strong> Exosomal markers of metabolic stress. Exosomes delivering AMPK-activating miRNAs for metabolic health.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Metabolic Pathway Network</h4>
            <div className="flex justify-center">
              <svg width="450" height="180" xmlns="http://www.w3.org/2000/svg">
                <style>{`
                  .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                  .insulin { fill: #4169E1; stroke: #000080; }
                  .mtor { fill: #32CD32; stroke: #228B22; }
                  .ampk { fill: #FF6347; stroke: #DC143C; }
                  .exosome { fill: #FFA07A; stroke: #CD5C5C; }
                `}</style>
                
                <rect x="50" y="60" width="80" height="40" className="insulin" rx="5"/>
                <text x="90" y="85" className="label" textAnchor="middle" fill="white" fontWeight="bold">Insulin/IGF-1</text>
                
                <rect x="185" y="60" width="80" height="40" className="mtor" rx="5"/>
                <text x="225" y="85" className="label" textAnchor="middle" fill="white" fontWeight="bold">mTOR</text>
                
                <rect x="320" y="60" width="80" height="40" className="ampk" rx="5"/>
                <text x="360" y="85" className="label" textAnchor="middle" fill="white" fontWeight="bold">AMPK</text>
                
                <circle cx="225" cy="140" r="15" className="exosome"/>
                <text x="225" y="145" className="label" textAnchor="middle" fontSize="8px">Exosome</text>
                <text x="225" y="170" className="label" textAnchor="middle">miR-126, miR-29, miR-21</text>
                
                <path d="M225,125 L90,100" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                <path d="M225,125 L225,100" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                <path d="M225,125 L360,100" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                
                <defs>
                  <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                    <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                  </marker>
                </defs>
              </svg>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pathways (Part 2): Sirtuins/NAD+, Nrf2 – Stress Resilience & Redox",
      subtitle: "Enhancing Stress Resistance & Antioxidant Defenses",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Sirtuins/NAD+ Pathway</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>NAD+-dependent deacylases (SIRT1-7) crucial for health, stress resistance, metabolism. NAD+ declines with age.</p>
                  <p><strong>Exosome Interaction:</strong> SIRT1/SIRT2 regulate exosome biogenesis/cargo. Exosomal miRNAs (e.g., miR-133a from myotubes) can silence Sirt1. SC-Exos can upregulate SIRT1.</p>
                  <p><strong>Clinical:</strong> Exosomal cargo as indicator of Sirtuin activity. SC-Exos to boost SIRT1 for resilience.</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Nrf2 Pathway</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p>Primary cellular defense against oxidative/electrophilic stress. Activity declines with age.</p>
                  <p><strong>Exosome Interaction:</strong> SC-Exos act as Nrf2 agonists by delivering miRNAs (e.g., miR-200a targeting Keap1) or circRNAs (e.g., circHIPK3 sponging Nrf2-inhibiting miRNAs).</p>
                  <p><strong>Clinical:</strong> Exosomal ncRNAs as biomarkers of oxidative stress/Nrf2 status. SC-Exos to bolster Nrf2 antioxidant defenses.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Stress Response Pathways</h4>
              <div className="flex justify-center">
                <svg width="350" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                    .sirtuin { fill: #9370DB; stroke: #663399; }
                    .nrf2 { fill: #FF8C00; stroke: #FF6347; }
                    .exosome { fill: #FFA07A; stroke: #CD5C5C; }
                    .nad { fill: #FFD700; }
                    .keap1 { fill: #DC143C; }
                  `}</style>
                  
                  <rect x="50" y="40" width="70" height="30" className="sirtuin" rx="5"/>
                  <text x="85" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">SIRT1</text>
                  
                  <circle cx="85" cy="90" r="8" className="nad"/>
                  <text x="85" y="95" className="label" textAnchor="middle" fontSize="8px">NAD+</text>
                  
                  <rect x="230" y="40" width="70" height="30" className="nrf2" rx="5"/>
                  <text x="265" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">Nrf2</text>
                  
                  <circle x="265" cy="90" r="8" className="keap1"/>
                  <text x="265" y="95" className="label" textAnchor="middle" fontSize="8px">Keap1</text>
                  
                  <circle cx="175" cy="140" r="20" className="exosome"/>
                  <text x="175" y="145" className="label" textAnchor="middle" fontSize="8px">SC-Exosome</text>
                  <text x="175" y="170" className="label" textAnchor="middle">miR-200a, circHIPK3</text>
                  
                  <path d="M155,130 L95,80" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                  <path d="M195,130 L255,80" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pathways (Part 3): NF-κB, p53/p16 – Inflammation & Senescence",
      subtitle: "Tackling Inflammaging & Cellular Senescence",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">NF-κB Pathway</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p>Master regulator of inflammation, immune response. Chronic activation drives "inflammaging."</p>
                  <p><strong>Exosome Interaction:</strong> Exosomes deliver NF-κB modulators. Exo-srIκB (inhibitor) ameliorates age-related neuroinflammation. MSC-Exos deliver miRNAs inhibiting NF-κB.</p>
                  <p><strong>Clinical:</strong> Exosomal NF-κB components as inflammatory biomarkers. Exosome-mediated delivery of NF-κB inhibitors.</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">p53/p16<sup>INK4a</sup> & Cellular Senescence</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p>Key regulators of cell cycle arrest/senescence. Senescent cells accumulate with age, release SASP (Senescence-Associated Secretory Phenotype). Exosomes are part of SASP.</p>
                  <p><strong>Exosome Interaction:</strong> p53 regulates exosome biogenesis/cargo (wild-type vs. mutant p53 have different effects). Exosomes shuttle p53 regulators or miRNAs targeting p53. "Senescence-associated exosomes" can propagate senescence.</p>
                  <p><strong>Clinical:</strong> Exosomal markers for senescence burden/p53 status. MSC-Exos to counteract senescence.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Inflammation & Senescence Network</h4>
              <div className="flex justify-center">
                <svg width="350" height="200" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                    .nfkb { fill: #DC143C; stroke: #8B0000; }
                    .p53 { fill: #9932CC; stroke: #800080; }
                    .senescent { fill: #FFB6C1; stroke: #FF69B4; }
                    .exosome { fill: #FFA07A; stroke: #CD5C5C; }
                    .sasp { fill: #FF4500; }
                  `}</style>
                  
                  <rect x="50" y="40" width="60" height="30" className="nfkb" rx="5"/>
                  <text x="80" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">NF-κB</text>
                  
                  <rect x="240" y="40" width="60" height="30" className="p53" rx="5"/>
                  <text x="270" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">p53/p16</text>
                  
                  <rect x="145" y="100" width="60" height="40" className="senescent" rx="5"/>
                  <text x="175" y="125" className="label" textAnchor="middle" fontSize="8px">Senescent Cell</text>
                  
                  <circle cx="100" cy="160" r="12" className="exosome"/>
                  <text x="100" y="165" className="label" textAnchor="middle" fontSize="8px">Exo-srIκB</text>
                  
                  <circle cx="250" cy="160" r="12" className="exosome"/>
                  <text x="250" y="165" className="label" textAnchor="middle" fontSize="8px">SASP-Exo</text>
                  
                  <circle cx="175" cy="25" r="8" className="sasp"/>
                  <text x="175" y="30" className="label" textAnchor="middle" fontSize="8px">SASP</text>
                  
                  <path d="M175,35 L175,100" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                  <path d="M112,155 L90,75" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                  <path d="M238,155 L280,75" stroke="#555" strokeWidth="1.5" markerEnd="url(#arrowhead)"/>
                  
                  <defs>
                    <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                      <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                    </marker>
                  </defs>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Pathways (Part 4): JAK/STAT, TGF-β, RAAS – Immunity, Fibrosis, Repair",
      subtitle: "Fine-Tuning Immunity, Fibrosis, and Tissue Remodeling",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">JAK/STAT Pathway</h3>
              <div className="space-y-2 text-sm text-blue-800">
                <p>Transmits cytokine/growth factor signals for immune regulation, cell growth. Dysregulation in cancer, immune disorders.</p>
                <p><strong>Exosome Interaction:</strong> Tumor-derived exosomes (TDEs) modulate JAK/STAT in immune cells. Exosomal miRNAs (e.g., miR-221, miR-375-3p) influence JAK/STAT. SC-Exos modulate JAK/STAT.</p>
                <p><strong>Clinical:</strong> Exosomal JAK/STAT indicators as biomarkers. Engineered exosomes for JAK/STAT modulation in autoimmune disease/cancer.</p>
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-green-900 mb-3">TGF-β Pathway</h3>
              <div className="space-y-2 text-sm text-green-800">
                <p>Regulates cell growth, differentiation, ECM homeostasis. Dysregulation in fibrosis, cancer. Overactivity with age.</p>
                <p><strong>Exosome Interaction:</strong> Exosomes carry TGF-β ligands (e.g., mast cell exosomes → prolonged SMAD2 signaling). Exosomal miRNAs (e.g., miR-133a) target TGF-β pathway components to reduce fibrosis.</p>
                <p><strong>Clinical:</strong> Exosomal TGF-β markers for fibrotic disease. Exosomes delivering anti-fibrotic miRNAs.</p>
              </div>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-purple-900 mb-3">RAAS (Renin-Angiotensin-Aldosterone System)</h3>
              <div className="space-y-2 text-sm text-purple-800">
                <p>Regulates BP, fluid balance. Overactivity linked to CVD, CKD.</p>
                <p><strong>Exosome Interaction (Primarily Biomarker):</strong> RAAS activation alters urinary exosome proteome (e.g., ↑ γENaC fragment).</p>
                <p><strong>Clinical:</strong> Urinary exosomal proteins (γENaC) as non-invasive RAAS activity/kidney effect markers to guide RAAS inhibitor therapy.</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Exosomes as Diagnostic Biomarkers",
      subtitle: "Liquid Biopsies: Exosomes as Windows into Health",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Advantages</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>Exosomes are present in easily accessible bodily fluids (blood, urine, saliva).</p>
                  <p>Their cargo (proteins, miRNAs, lncRNAs, lipids) reflects the physiological/pathological state of origin cells.</p>
                  <ul className="ml-4 space-y-1">
                    <li>• <strong>Non-invasive</strong></li>
                    <li>• <strong>Real-time information</strong></li>
                    <li>• <strong>Protect cargo from degradation</strong></li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Applications in Longevity & Functional Medicine</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <ul className="space-y-1">
                    <li>• Early detection of age-related diseases</li>
                    <li>• Monitoring disease progression and treatment response</li>
                    <li>• Assessing systemic inflammation, oxidative stress, metabolic health</li>
                    <li>• Personalizing interventions based on individual exosomal profiles</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Examples of Potential Exosomal Biomarkers</h4>
              <div className="overflow-x-auto">
                <svg width="550" height="220" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .header { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; fill: #FFF; }
                    .cell-text { font-family: Arial, sans-serif; font-size: 11px; fill: #333; }
                    .rect-header { fill: #4682B4; }
                    .rect-cell { fill: #F0F8FF; stroke: #D3D3D3; }
                  `}</style>
                  
                  <rect x="10" y="10" width="530" height="30" className="rect-header"/>
                  <text x="20" y="30" className="header">Pathway/Condition</text>
                  <text x="180" y="30" className="header">Exosomal Biomarker</text>
                  <text x="370" y="30" className="header">Clinical Implication</text>

                  <rect x="10" y="40" width="160" height="40" className="rect-cell"/>
                  <text x="15" y="65" className="cell-text">Insulin Resistance</text>
                  <rect x="170" y="40" width="190" height="40" className="rect-cell"/>
                  <text x="175" y="57" className="cell-text">Specific proteins (cord blood),</text>
                  <text x="175" y="72" className="cell-text">miRNAs (e.g., affecting IIS)</text>
                  <rect x="360" y="40" width="180" height="40" className="rect-cell"/>
                  <text x="365" y="57" className="cell-text">Early risk prediction (T2D)</text>
                  <text x="365" y="72" className="cell-text">(Ref: 66, 67)</text>

                  <rect x="10" y="80" width="160" height="40" className="rect-cell"/>
                  <text x="15" y="105" className="cell-text">Inflammation (NF-κB)</text>
                  <rect x="170" y="80" width="190" height="40" className="rect-cell"/>
                  <text x="175" y="97" className="cell-text">NF-κB pathway components,</text>
                  <text x="175" y="112" className="cell-text">Inflammatory miRNAs</text>
                  <rect x="360" y="80" width="180" height="40" className="rect-cell"/>
                  <text x="365" y="97" className="cell-text">Monitor inflammatory status,</text>
                  <text x="365" y="112" className="cell-text">therapy response (Ref: 23)</text>

                  <rect x="10" y="120" width="160" height="40" className="rect-cell"/>
                  <text x="15" y="145" className="cell-text">Oxidative Stress (Nrf2)</text>
                  <rect x="170" y="120" width="190" height="40" className="rect-cell"/>
                  <text x="175" y="137" className="cell-text">Keap1-targeting miRNAs</text>
                  <text x="175" y="152" className="cell-text">(e.g., miR-200a)</text>
                  <rect x="360" y="120" width="180" height="40" className="rect-cell"/>
                  <text x="365" y="137" className="cell-text">Assess redox balance,</text>
                  <text x="365" y="152" className="cell-text">Nrf2 activity (Ref: 84)</text>

                  <rect x="10" y="160" width="160" height="40" className="rect-cell"/>
                  <text x="15" y="185" className="cell-text">RAAS Activity (Kidney)</text>
                  <rect x="170" y="160" width="190" height="40" className="rect-cell"/>
                  <text x="175" y="177" className="cell-text">Urinary exosomal γENaC</text>
                  <text x="175" y="192" className="cell-text">fragment</text>
                  <rect x="360" y="160" width="180" height="40" className="rect-cell"/>
                  <text x="365" y="177" className="cell-text">Non-invasive marker of</text>
                  <text x="365" y="192" className="cell-text">aldosterone effect (Ref: 117)</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Therapeutic Exosomes - Source & Quality Control",
      subtitle: "Therapeutic Exosomes: Source, Purity, Potency",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Source is Critical</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Mesenchymal Stem Cells (MSCs):</strong> Common source (bone marrow, adipose, umbilical cord). Known for regenerative and immunomodulatory properties.</p>
                  <p><strong>"Young" vs. "Aged" Cells:</strong> Exosomes from young, healthy cells theoretically more beneficial.</p>
                  <p><strong>Other Sources:</strong> Platelets, immune cells, plant-derived (research ongoing).</p>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Quality Control & Standardization (Major Challenge)</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Isolation Methods:</strong> Ultracentrifugation, precipitation, size-exclusion chromatography, microfluidics. Method affects purity and yield.</p>
                  <p><strong>Characterization:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Size and concentration (NTA - Nanoparticle Tracking Analysis)</li>
                    <li>• Morphology (electron microscopy)</li>
                    <li>• Protein markers (CD9, CD63, CD81, Alix, TSG101)</li>
                    <li>• Absence of contaminants (endotoxins, mycoplasma)</li>
                  </ul>
                  <p><strong>Potency Assays:</strong> Functional assays relevant to the intended therapeutic effect (e.g., immune modulation, angiogenesis).</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">"Off-the-shelf" Products</h3>
                <p className="text-sm text-purple-800">
                  Require rigorous source control and characterization for consistent effects. Variability is a concern.
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Quality Control Pipeline</h4>
                <div className="flex justify-center">
                  <svg width="400" height="200" xmlns="http://www.w3.org/2000/svg">
                    <style>{`
                      .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                      .source { fill: #4CAF50; stroke: #388E3C; }
                      .isolation { fill: #2196F3; stroke: #1976D2; }
                      .analysis { fill: #FF9800; stroke: #F57C00; }
                      .product { fill: #9C27B0; stroke: #7B1FA2; }
                    `}</style>
                    
                    <rect x="20" y="40" width="70" height="30" className="source" rx="5"/>
                    <text x="55" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">MSC Source</text>
                    
                    <rect x="120" y="40" width="70" height="30" className="isolation" rx="5"/>
                    <text x="155" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">Isolation</text>
                    
                    <rect x="220" y="40" width="70" height="30" className="analysis" rx="5"/>
                    <text x="255" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">Analysis</text>
                    
                    <rect x="320" y="40" width="70" height="30" className="product" rx="5"/>
                    <text x="355" y="60" className="label" textAnchor="middle" fill="white" fontWeight="bold">QC Product</text>
                    
                    <path d="M90,55 L115,55" stroke="#555" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                    <path d="M190,55 L215,55" stroke="#555" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                    <path d="M290,55 L315,55" stroke="#555" strokeWidth="2" markerEnd="url(#arrowhead)"/>
                    
                    <text x="55" y="90" className="label" textAnchor="middle">Young, Healthy</text>
                    <text x="155" y="90" className="label" textAnchor="middle">UC, SEC, PEG</text>
                    <text x="255" y="90" className="label" textAnchor="middle">NTA, EM, WB</text>
                    <text x="355" y="90" className="label" textAnchor="middle">Therapeutic Grade</text>
                    
                    <defs>
                      <marker id="arrowhead" markerWidth="8" markerHeight="6" refX="0" refY="3" orient="auto">
                        <polygon points="0 0, 8 3, 0 6" fill="#555"/>
                      </marker>
                    </defs>
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Dosing, Administration & Protocols",
      subtitle: "Dosing Principles & Administration Routes",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Dosing Considerations</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Dosing is Highly Variable & Evolving:</strong> No universal standard. Based on preclinical data, early trials, empirical evidence.</p>
                  <p><strong>Units:</strong> Typically particle count (e.g., 1×10⁹ to 1×10¹² particles) or protein amount.</p>
                  <p><strong>Factors Influencing Dose & Protocol:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Source & Potency: Different sources/preparations have different activities</li>
                    <li>• Patient Condition & Size: Severity, chronicity, target tissue</li>
                    <li>• Route of Administration</li>
                    <li>• Frequency: Single dose to multiple doses over weeks/months</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Administration Routes</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Intravenous (IV):</strong> Systemic effects (inflammation, immune modulation, general rejuvenation)</p>
                  <p><strong>Local Injection:</strong> Targeted delivery (intra-articular for OA, intradermal for skin)</p>
                  <p><strong>Topical:</strong> Skin conditions, wound healing</p>
                  <p><strong>Intranasal:</strong> Potential for CNS delivery</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Dosing & Administration Considerations</h4>
              <div className="overflow-x-auto">
                <svg width="550" height="180" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .header { font-family: Arial, sans-serif; font-size: 12px; font-weight: bold; fill: #FFF; }
                    .cell-text { font-family: Arial, sans-serif; font-size: 11px; fill: #333; }
                    .rect-header { fill: #2E8B57; }
                    .rect-cell { fill: #F5FFFA; stroke: #D3D3D3; }
                  `}</style>
                  
                  <rect x="10" y="10" width="530" height="30" className="rect-header"/>
                  <text x="20" y="30" className="header">Factor</text>
                  <text x="150" y="30" className="header">Consideration / Example</text>
                  <text x="400" y="30" className="header">Clinical Relevance</text>

                  <rect x="10" y="40" width="130" height="40" className="rect-cell"/>
                  <text x="15" y="65" className="cell-text">Exosome Source</text>
                  <rect x="140" y="40" width="250" height="40" className="rect-cell"/>
                  <text x="145" y="57" className="cell-text">MSC (bone marrow, adipose, UC)</text>
                  <text x="145" y="72" className="cell-text">"Young" vs. "Aged" donor cells</text>
                  <rect x="390" y="40" width="150" height="40" className="rect-cell"/>
                  <text x="395" y="57" className="cell-text">Potency, immunomodulatory</text>
                  <text x="395" y="72" className="cell-text">capacity (Ref: 2)</text>

                  <rect x="10" y="80" width="130" height="40" className="rect-cell"/>
                  <text x="15" y="105" className="cell-text">Target Condition</text>
                  <rect x="140" y="80" width="250" height="40" className="rect-cell"/>
                  <text x="145" y="97" className="cell-text">OA, skin aging, systemic</text>
                  <text x="145" y="112" className="cell-text">inflammation, neurodegeneration</text>
                  <rect x="390" y="80" width="150" height="40" className="rect-cell"/>
                  <text x="395" y="97" className="cell-text">Determines route, dose,</text>
                  <text x="395" y="112" className="cell-text">frequency</text>

                  <rect x="10" y="120" width="130" height="40" className="rect-cell"/>
                  <text x="15" y="145" className="cell-text">Administration Route</text>
                  <rect x="140" y="120" width="250" height="40" className="rect-cell"/>
                  <text x="145" y="137" className="cell-text">IV, Intra-articular, Intradermal,</text>
                  <text x="145" y="152" className="cell-text">Topical, Intranasal</text>
                  <rect x="390" y="120" width="150" height="40" className="rect-cell"/>
                  <text x="395" y="137" className="cell-text">Systemic vs. Local effect,</text>
                  <text x="395" y="152" className="cell-text">bioavailability (Ref: 23)</text>
                </svg>
              </div>
              
              <div className="mt-6 bg-yellow-50 p-4 rounded-lg">
                <h4 className="font-semibold text-yellow-900 mb-2">Example Protocol Considerations (General):</h4>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p><strong>Osteoarthritis (Intra-articular):</strong> 1×10¹⁰ - 1×10¹² particles/joint, repeat every 3-12 months</p>
                  <p><strong>Systemic Anti-inflammatory/Longevity (IV):</strong> 1×10¹⁰ - 1×10¹² particles, quarterly to annually</p>
                  <p><strong>Neuroinflammation (Intranasal/IV):</strong> Dosing still highly experimental</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Safety, Monitoring & Contraindications",
      subtitle: "Safety Considerations & Monitoring Protocols",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-red-900 mb-3">Safety Profile & Concerns</h3>
                <div className="space-y-2 text-sm text-red-800">
                  <p><strong>Generally Well-Tolerated:</strong> Early clinical data suggests good safety profile, minimal adverse events.</p>
                  <p><strong>Theoretical Risks:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Immune reactions (though MSC-derived exosomes are generally immunosuppressive)</li>
                    <li>• Contamination (if not properly isolated/sterilized)</li>
                    <li>• Unintended biological effects (cargo variability)</li>
                    <li>• Potential for promoting malignancy (theoretical, limited evidence)</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Contraindications</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p><strong>Relative Contraindications:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Active malignancy (theoretical concern about promoting cancer)</li>
                    <li>• Severe immunocompromised state</li>
                    <li>• Known hypersensitivity to components</li>
                    <li>• Pregnancy/breastfeeding (insufficient data)</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Monitoring & Follow-up</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Baseline Assessment:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Complete medical history</li>
                    <li>• Physical examination</li>
                    <li>• Relevant laboratory tests</li>
                    <li>• Imaging if indicated</li>
                  </ul>
                  <p><strong>Post-Treatment Monitoring:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Short-term: Monitor for immediate reactions</li>
                    <li>• Long-term: Track clinical outcomes, biomarkers</li>
                    <li>• Functional assessments relevant to indication</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Patient Consent & Education</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p>Comprehensive informed consent covering:</p>
                  <ul className="ml-4 space-y-1">
                    <li>• Experimental nature of therapy</li>
                    <li>• Potential benefits and risks</li>
                    <li>• Alternative treatments available</li>
                    <li>• Cost considerations</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Clinical Application: Combination Therapies & Integration",
      subtitle: "Synergistic Approaches in Longevity Medicine",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Combination Strategies</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>With Other Regenerative Therapies:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• PRP (Platelet-Rich Plasma)</li>
                    <li>• Stem cell therapy</li>
                    <li>• Peptide therapy</li>
                    <li>• NAD+ optimization</li>
                  </ul>
                  <p><strong>With Lifestyle Interventions:</strong></p>
                  <ul className="ml-4 space-y-1">
                    <li>• Exercise protocols</li>
                    <li>• Nutritional optimization</li>
                    <li>• Sleep optimization</li>
                    <li>• Stress management</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Sequential vs. Concurrent</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Sequential Approach:</strong> Staged interventions with monitoring between phases</p>
                  <p><strong>Concurrent Approach:</strong> Simultaneous therapies with careful monitoring for interactions</p>
                  <p><strong>Considerations:</strong> Timing, dose adjustments, monitoring complexity</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-4 text-center">Integration Framework</h4>
              <div className="flex justify-center">
                <svg width="400" height="220" xmlns="http://www.w3.org/2000/svg">
                  <style>{`
                    .label { font-family: Arial, sans-serif; font-size: 10px; fill: #333; }
                    .exosome { fill: #FF6B6B; stroke: #E55151; }
                    .prp { fill: #4ECDC4; stroke: #45B7B8; }
                    .peptide { fill: #45B7D1; stroke: #3D5AA1; }
                    .lifestyle { fill: #96CEB4; stroke: #6FA082; }
                    .nad { fill: #FECA57; stroke: #FF9F43; }
                  `}</style>
                  
                  <circle cx="200" cy="110" r="25" className="exosome"/>
                  <text x="200" y="118" className="label" textAnchor="middle" fontWeight="bold">Exosomes</text>
                  
                  <circle cx="120" cy="60" r="18" className="prp"/>
                  <text x="120" y="67" className="label" textAnchor="middle" fontSize="8px">PRP</text>
                  
                  <circle cx="280" cy="60" r="18" className="peptide"/>
                  <text x="280" y="67" className="label" textAnchor="middle" fontSize="8px">Peptides</text>
                  
                  <circle cx="120" cy="160" r="18" className="lifestyle"/>
                  <text x="120" y="167" className="label" textAnchor="middle" fontSize="8px">Lifestyle</text>
                  
                  <circle cx="280" cy="160" r="18" className="nad"/>
                  <text x="280" y="167" className="label" textAnchor="middle" fontSize="8px">NAD+</text>
                  
                  <path d="M175,95 L135,70" stroke="#555" strokeWidth="1.5"/>
                  <path d="M225,95 L265,70" stroke="#555" strokeWidth="1.5"/>
                  <path d="M175,125 L135,150" stroke="#555" strokeWidth="1.5"/>
                  <path d="M225,125 L265,150" stroke="#555" strokeWidth="1.5"/>
                  
                  <text x="200" y="30" className="label" textAnchor="middle" fontWeight="bold">Synergistic Longevity Protocol</text>
                  <text x="200" y="200" className="label" textAnchor="middle">Enhanced therapeutic outcomes</text>
                </svg>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Future Directions & Research Priorities",
      subtitle: "The Evolving Landscape of Exosome Medicine",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Near-term Research Priorities</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <ul className="space-y-1">
                    <li>• Standardization of isolation, characterization methods</li>
                    <li>• Optimal dosing protocols and administration routes</li>
                    <li>• Biomarker validation for clinical applications</li>
                    <li>• Safety profile establishment in larger cohorts</li>
                    <li>• Cost-effectiveness analyses</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Long-term Innovations</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <ul className="space-y-1">
                    <li>• Engineered exosomes with targeted cargo</li>
                    <li>• Personalized exosome therapies</li>
                    <li>• Synthetic exosome mimetics</li>
                    <li>• AI-guided cargo optimization</li>
                    <li>• Oral/topical delivery systems</li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Regulatory Landscape</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Current Status:</strong> Investigational therapies, limited FDA approval</p>
                  <p><strong>Challenges:</strong> Classification, manufacturing standards, quality control</p>
                  <p><strong>Evolution:</strong> Moving toward structured regulatory pathways</p>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Market Projections</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p><strong>Growth Trajectory:</strong> Rapid expansion expected in regenerative medicine market</p>
                  <p><strong>Applications:</strong> Expanding from research to clinical practice</p>
                  <p><strong>Accessibility:</strong> Improving with standardization and scale</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "SHIFT Framework Lens: Exosomes Through the Longevity Medicine Paradigm",
      subtitle: "Comprehensive Analysis Using the SHIFT Framework",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-blue-900">The SHIFT Framework Applied to Exosomes</h3>
              <span className="bg-blue-200 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">SHIFT Analysis</span>
            </div>
            <p className="text-sm text-blue-800">
              Exosomes represent a paradigm shift in regenerative medicine, offering both diagnostic and therapeutic potential 
              that aligns perfectly with the SHIFT framework's principles of precision, personalization, and systems-based approaches.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
                <h4 className="text-lg font-semibold text-green-900 mb-3 flex items-center">
                  <span className="bg-green-200 text-green-800 px-2 py-1 rounded mr-3 text-xs">S</span>
                  Systems-Based Medicine
                </h4>
                <div className="space-y-2 text-sm text-green-800">
                  <p><strong>Intercellular Communication Networks:</strong> Exosomes facilitate system-wide communication between organs and tissues</p>
                  <p><strong>Multi-pathway Modulation:</strong> Single intervention affects multiple aging pathways (mTOR, AMPK, NF-κB, Nrf2)</p>
                  <p><strong>Biomarker Integration:</strong> Liquid biopsies provide systems-level health insights</p>
                </div>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
                <h4 className="text-lg font-semibold text-purple-900 mb-3 flex items-center">
                  <span className="bg-purple-200 text-purple-800 px-2 py-1 rounded mr-3 text-xs">H</span>
                  Health Optimization
                </h4>
                <div className="space-y-2 text-sm text-purple-800">
                  <p><strong>Regenerative Potential:</strong> Tissue repair, immune modulation, metabolic enhancement</p>
                  <p><strong>Preventive Applications:</strong> Early biomarker detection for intervention before disease onset</p>
                  <p><strong>Performance Enhancement:</strong> Optimizing cellular function and resilience</p>
                </div>
              </div>
              
              <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-500">
                <h4 className="text-lg font-semibold text-yellow-900 mb-3 flex items-center">
                  <span className="bg-yellow-200 text-yellow-800 px-2 py-1 rounded mr-3 text-xs">I</span>
                  Individualized Approaches
                </h4>
                <div className="space-y-2 text-sm text-yellow-800">
                  <p><strong>Personal Exosomal Profiles:</strong> Individual biomarker patterns guide therapy selection</p>
                  <p><strong>Autologous vs. Allogeneic:</strong> Personalized source selection based on individual needs</p>
                  <p><strong>Dosing Optimization:</strong> Tailored protocols based on patient response and biomarkers</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-500">
                <h4 className="text-lg font-semibold text-red-900 mb-3 flex items-center">
                  <span className="bg-red-200 text-red-800 px-2 py-1 rounded mr-3 text-xs">F</span>
                  Functional Medicine Integration
                </h4>
                <div className="space-y-2 text-sm text-red-800">
                  <p><strong>Root Cause Targeting:</strong> Address underlying aging mechanisms rather than symptoms</p>
                  <p><strong>Holistic Assessment:</strong> Combine with nutrition, lifestyle, and other interventions</p>
                  <p><strong>Timeline Consideration:</strong> Long-term regenerative effects vs. acute interventions</p>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center">
                  <span className="bg-blue-200 text-blue-800 px-2 py-1 rounded mr-3 text-xs">T</span>
                  Technology Integration
                </h4>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Advanced Characterization:</strong> NTA, flow cytometry, multi-omics analysis</p>
                  <p><strong>Manufacturing Innovation:</strong> Bioreactors, purification technologies, quality control</p>
                  <p><strong>Delivery Optimization:</strong> Targeted delivery systems and administration routes</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Clinical Implementation</h4>
                <div className="space-y-2 text-sm text-gray-800">
                  <p><strong>Assessment:</strong> Baseline exosomal biomarker profiling</p>
                  <p><strong>Intervention:</strong> Targeted exosome therapy with monitoring</p>
                  <p><strong>Optimization:</strong> Adjust based on response and biomarker changes</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Key Takeaways & Clinical Implementation",
      subtitle: "Translating Exosome Science into Practice",
      content: (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Core Clinical Insights</h3>
                <div className="space-y-2 text-sm text-green-800">
                  <ul className="space-y-1">
                    <li>• Exosomes are nature's nanomessengers with dual diagnostic and therapeutic potential</li>
                    <li>• They modulate key aging pathways through cargo delivery (miRNAs, proteins, lipids)</li>
                    <li>• Source quality and characterization are critical for therapeutic applications</li>
                    <li>• Integration with lifestyle and other interventions enhances outcomes</li>
                    <li>• Personalized approaches based on individual biomarker profiles</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Implementation Strategy</h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <p><strong>Phase 1:</strong> Education and infrastructure development</p>
                  <p><strong>Phase 2:</strong> Biomarker implementation and basic protocols</p>
                  <p><strong>Phase 3:</strong> Advanced therapeutic applications</p>
                  <p><strong>Phase 4:</strong> Personalized protocols and optimization</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-purple-900 mb-3">Practice Integration Checklist</h3>
                <div className="space-y-2 text-sm text-purple-800">
                  <div className="space-y-1">
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4"/>
                      <span>Establish quality control protocols</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4"/>
                      <span>Develop informed consent procedures</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4"/>
                      <span>Create monitoring and follow-up protocols</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4"/>
                      <span>Establish relationships with reputable suppliers</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input type="checkbox" className="w-4 h-4"/>
                      <span>Integrate with existing longevity protocols</span>
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-orange-900 mb-3">Next Steps</h3>
                <div className="space-y-2 text-sm text-orange-800">
                  <p>Continue education through peer-reviewed literature and professional development opportunities</p>
                  <p>Network with colleagues implementing exosome therapies</p>
                  <p>Consider pilot programs with select patients</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "Q&A and References",
      subtitle: "Discussion and Further Reading",
      content: (
        <div className="space-y-6">
          <div className="bg-blue-50 p-6 rounded-lg">
            <h3 className="text-xl font-bold text-blue-900 mb-4">Questions & Discussion</h3>
            <p className="text-sm text-blue-800 mb-4">
              We welcome your questions about exosome applications in longevity medicine, 
              clinical implementation strategies, and integration with existing protocols.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Common Questions</h4>
              <div className="space-y-3 text-sm text-gray-800">
                <div>
                  <p className="font-medium">Q: How do I evaluate exosome product quality?</p>
                  <p>A: Look for NTA characterization, protein markers (CD9/63/81), sterility testing, and functional assays.</p>
                </div>
                <div>
                  <p className="font-medium">Q: What's the optimal dosing frequency?</p>
                  <p>A: Varies by indication and source; typically ranges from single treatments to quarterly protocols.</p>
                </div>
                <div>
                  <p className="font-medium">Q: How do I integrate with existing protocols?</p>
                  <p>A: Start with biomarker assessment, then consider combination with lifestyle and other regenerative therapies.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-lg">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Key References</h4>
              <div className="space-y-2 text-xs text-gray-700">
                <p>1. Théry C, et al. Minimal information for studies of extracellular vesicles 2018 (MISEV2018). J Extracell Vesicles. 2018;7(1):1535750.</p>
                <p>2. Lai RC, et al. Exosome secreted by MSC reduces myocardial ischemia/reperfusion injury. Stem Cell Res. 2010;4(3):214-222.</p>
                <p>3. Zhang B, et al. Exosomes from young and aged mice and their effect on inflammation. Signal Transduct Target Ther. 2017;2:17006.</p>
                <p>4. Witwer KW, et al. Standardization of sample collection, isolation and analysis methods in extracellular vesicle research. J Extracell Vesicles. 2013;2.</p>
                <p>5. Kalluri R, LeBleu VS. The biology, function, and biomedical applications of exosomes. Science. 2020;367(6478):eaau6977.</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 p-6 rounded-lg text-center">
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Thank You</h4>
            <p className="text-sm text-gray-700">
              For additional resources and continued education in longevity medicine, 
              visit our mentorship platform and explore our comprehensive clinical protocols.
            </p>
          </div>
        </div>
      )
    }
  ];

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "ArrowLeft" && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      } else if (event.key === "ArrowRight" && currentSlide < slides.length - 1) {
        setCurrentSlide(currentSlide + 1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentSlide, slides.length]);

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h1>
          <p className="text-gray-600">Please sign in to access this lecture.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/mentorship" className="text-gray-500 hover:text-gray-700 text-sm font-medium flex items-center">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Mentorship
                </Link>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <span className="text-gray-900 text-sm font-medium">
                  Exosomes in Longevity Medicine
                </span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Slide Content */}
          <div className="px-8 py-12 min-h-[600px]">
            {slides[currentSlide].content}
          </div>

          {/* Navigation */}
          <div className="bg-gray-50 px-6 py-4 flex items-center justify-between">
            <button
              onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
              disabled={currentSlide === 0}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Previous
            </button>

            <div className="flex space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full ${
                    index === currentSlide ? "bg-blue-600" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            <button
              onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
              disabled={currentSlide === slides.length - 1}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* Embedded Audio Lecture */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Audio Lecture</h2>
              <p className="text-gray-600 mt-1">Listen to the comprehensive audio presentation</p>
            </div>
            <div className="bg-blue-50 px-4 py-2 rounded-lg">
              <span className="text-blue-800 text-sm font-medium">Enhanced Learning</span>
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <iframe
              src="https://share.descript.com/embed/EUqz8slCYUo"
              width="100%"
              height="360"
              frameBorder="0"
              allowFullScreen
              className="rounded-lg shadow-sm"
              title="Exosomes in Longevity Medicine Audio Lecture"
            ></iframe>
          </div>
          
          <div className="mt-4 text-sm text-gray-600">
            <p className="flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              Audio lecture covers all 22 slides with detailed explanations and clinical insights
            </p>
          </div>
        </div>
      </div>

      {/* Keyboard Navigation */}
      <div className="fixed bottom-4 right-4 bg-white p-2 rounded-lg shadow-md">
        <p className="text-xs text-gray-500 font-light">Use ← → arrows to navigate</p>
      </div>
    </div>
  );
}